import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Button, Col, Container, Row } from "react-bootstrap"
import { injectIntl } from "react-intl"
import { useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { Redirect } from "react-router"
import SideBar from "../../common/layout/sidebar.component"
import ListingWrapper from "../../common/listing/ListingWrapper"
import LoadingSpinner from "../../common/spinner/loading.component"
import { GlobalHelper } from "../../indexImports"
import PaymentAccount from "../../payment-account/model/paymentaccount"
import { Intl } from "../../state/helpers/IntlProps"
import { setMerchantID } from "../../state/payment-processor/reducer"
import { RootState } from "../../state/store"
import Terminal from "../../state/terminal/Terminal"
import TerminalService from "../../state/terminal/TerminalService"
import PaymentProcessingTabs from "../PaymentProcessingTabs"
import TerminalBadges from "../components/TerminalBadges"
import RegisterTerminalModal from "../modals/RegisterTerminalModal"
import TerminalDetailsModal from "../modals/TerminalDetailsModal"
import PaymentAccountSelect from "../payment-listing/PaymentAccountSelect"
import { useGetPaymentAccounts } from "../usePaymentAccountSession"
import NoTerminals from "./NoTerminals"
import { messages } from "./messages"

type Props = {
    intl: Intl
    paymentAccountId: string
}

const TerminalListing = (props: Props) => {
    SideBar.activateMenu("payment-processing")
    const merchantID = useSelector((state: RootState) => state.paymentProcessing.merchantID)
    const dispatch = useDispatch()
    const [currentPaymentAccount, setCurrentPaymentAccount] = useState<PaymentAccount | undefined>(undefined)
    const { paymentAccounts,
        merchantID : defaultMerchantID,
        isLoading: isLoadingPaymentAccounts,
        hasActiveAccount } = useGetPaymentAccounts()
    const {data : terminals, isLoading: isLoadingTerminals, refetch} = useQuery(["terminals-listing", paymentAccounts], () => {
        if (!currentPaymentAccount) {
            const pa = paymentAccounts?.find(pa => pa.isActive())
            setCurrentPaymentAccount(pa)
            return pa ? TerminalService.listForPaymentAccount(pa.getId()) : []
        } else {
            return(TerminalService.listForPaymentAccount(currentPaymentAccount.getId()))
        }
    })
    const [showRegisterModal, setShowRegisterModal] = useState<boolean>(false)
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false)
    const [terminalDetailsToShow, setTerminalDetailsToShow] = useState<Terminal | null>(null)

    useEffect(() => {
        if (!merchantID) {
            dispatch(setMerchantID(paymentAccounts?.find(pa => pa.isActive())?.getMerchantId()
                || defaultMerchantID))
        }
    }, [paymentAccounts])

    useEffect(() => {
        const pa = paymentAccounts?.find(pa => pa.getMerchantId() === merchantID)
        setCurrentPaymentAccount(pa)
    }, [merchantID])

    useEffect(() => {
        refetch()
    }, [currentPaymentAccount])

    const headers = {
        data: [
            props.intl.formatMessage(messages.deviceNameHeader),
            props.intl.formatMessage(messages.deviceModelHeader),
            props.intl.formatMessage(messages.deviceStatusHeader),
        ],
        classNames: [],
    }

    const rows = terminals
        ?.sort((a, b) => a.getName().localeCompare(b.getName()))
        .map(terminal => ({
            rowId: terminal.getId(),
            data: [
                <a className="link-primary" onClick={() => handleOpenDetailsModal(terminal)} key={`${terminal.getId()}-name`}>
                    {terminal.getName()}
                </a>,
                terminal.getBrandAndModel(),
                <TerminalBadges key={`${terminal.getId()}-badges`} terminal={terminal} />,
            ]
        })) ?? []

    const standardList = {
        className: "row pt-3",
        headers: headers,
        rows: rows,
        pageSize: 1,
        totalItemsCount: terminals?.length ?? 0,
        showSelectionSize: false,
    }

    const handleOpenDetailsModal = (terminal: Terminal) => {
        setTerminalDetailsToShow(terminal)
        setShowDetailsModal(true)
    }

    const handleOpenRegisterModal = () => {
        setShowRegisterModal(true)
    }

    const handleModalClose = (success: boolean, handler: (value: boolean) => void) => {
        handler(false)

        if (success) {
            refetch()
        }
    }

    const hasAllowedCurrency = GlobalHelper.getCountryCodeFromCurrency(AppContext.getCurrency()) === "US"

    return (
        <>
            {!hasAllowedCurrency && <Redirect to={"/"}/>}
            {hasAllowedCurrency && (
                <>
                    <PaymentProcessingTabs hasActiveAccount={hasActiveAccount} />
                    {isLoadingPaymentAccounts && <LoadingSpinner />}
                    {!isLoadingPaymentAccounts && !hasActiveAccount && <Redirect to={"/payment-processing/accounts"}/>}
                    {!isLoadingPaymentAccounts && hasActiveAccount && (
                        <>
                            <Container className="p-0" fluid>
                                <Row>
                                    <Col md={4}>
                                        <h2 className="m-0 custom-lead">
                                            {props.intl.formatMessage(messages.listingHeader)}
                                        </h2>
                                    </Col>
                                    <Col md={{span: 2, offset: 4}}>
                                        <PaymentAccountSelect/>
                                    </Col>
                                    <Col md={{span: 2}}>
                                        <Button
                                            onClick={handleOpenRegisterModal}
                                            className="btn btn-primary col-12"
                                            disabled={isLoadingPaymentAccounts}
                                        >
                                            {isLoadingPaymentAccounts && <LoadingSpinner height={18} />}
                                            {!isLoadingPaymentAccounts &&  props.intl.formatMessage(messages.newTerminalButton)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Container>
                            <RegisterTerminalModal
                                show={showRegisterModal}
                                paymentAccount={currentPaymentAccount}
                                onClose={(e: boolean) => handleModalClose(e, setShowRegisterModal)}
                            />
                            {terminalDetailsToShow && (
                                <TerminalDetailsModal
                                    show={showDetailsModal}
                                    terminal={terminalDetailsToShow}
                                    onClose={(e: boolean) => handleModalClose(e, setShowDetailsModal)}
                                />
                            )}
                            {isLoadingTerminals || standardList.rows.length ? (
                                <ListingWrapper
                                    standardListProps={standardList}
                                    isLoading={isLoadingTerminals}
                                />
                            ) : (
                                <NoTerminals />
                            )}
                        </>
                    )}
                </>
            )}
        </>
    )
}

export default injectIntl(TerminalListing)


// WEBPACK FOOTER //
// ./src/payment-processing/terminal-listing/TerminalListing.tsx